// ---------------------------------------------------------------------------
// Accessify Icon System
// ---------------------------------------------------------------------------
// Inline SVG strings (CSP-friendly, no external loads).
//
// Paths sourced from the Untitled UI icon library — the same library the
// Dashboard-v2 uses (apps/dashboard-v2 → @accessify/components → @untitledui/
// icons). Every feature icon below corresponds to a real Untitled UI name
// (Contrast03, Type01, Link01, Image03, ImageX, …); the path data was lifted
// 1:1 from node_modules/@untitledui/icons/dist/<Name>.mjs so visual identity
// matches the Dashboard exactly.
//
// Default viewBox: 0 0 24 24 | stroke="currentColor" stroke-width="2"
// stroke-linecap="round" stroke-linejoin="round" — Untitled UI defaults.
// Every SVG carries aria-hidden="true" so assistive tech skips the decorative
// graphic and reads the accompanying label instead.
// ---------------------------------------------------------------------------

const SVG_OPEN =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
const SVG_CLOSE = '</svg>';

/** Helper - wrap paths in the common SVG shell. */
function wrap(inner: string): string {
  return `${SVG_OPEN}${inner}${SVG_CLOSE}`;
}

// ---------------------------------------------------------------------------
// Individual icon functions
// ---------------------------------------------------------------------------

/**
 * Trigger glyph — the brand "person inside a ring" mark from
 * docs/brand/widget-icon.svg. Uses its own 64×64 viewBox (instead of the
 * 24×24 default of `wrap()`) and renders with currentColor so the trigger
 * button's `color` (white by default, overridable via brandTriggerIconColor)
 * drives the glyph fill. The 1.45× scale around (32, 32) matches the
 * proportions of the standalone brand icon asset.
 */
export function iconAccessibility(): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="currentColor" aria-hidden="true">' +
      '<g transform="translate(32 32) scale(1.45) translate(-32 -32)">' +
        '<circle cx="32" cy="32" r="14" fill="none" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke"/>' +
        '<circle cx="32" cy="23" r="2.2"/>' +
        '<path d="M24 26.5 h16 a1 1 0 0 1 0 2 h-6.4 v3 l2.4 10 a1.2 1.2 0 0 1 -2.4 0.4 l-1.6 -7 -1.6 7 a1.2 1.2 0 0 1 -2.4 -0.4 l2.4 -10 v-3 h-6.4 a1 1 0 0 1 0 -2 z"/>' +
      '</g>' +
    '</svg>'
  );
}

/** Untitled UI · Contrast03 */
export function iconContrast(): string {
  return wrap(
    '<path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"/>' +
    '<path d="M16 8.5a7.5 7.5 0 0 1-9.284 7.287 6.5 6.5 0 1 0 9.07-9.07c.14.571.214 1.168.214 1.783Z" fill="currentColor" stroke="none"/>'
  );
}

/** Untitled UI · Type01 */
export function iconTextSize(): string {
  return wrap(
    '<path d="M4 7c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C5.602 4 6.068 4 7 4h10c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C20 5.602 20 6.068 20 7M9 20h6M12 4v16"/>'
  );
}

/** Untitled UI · AlignHorizontalCentre02 — line height / vertical spacing */
export function iconSpacing(): string {
  return wrap(
    '<path d="M4 9h16M4 15h16M10 5l-2 2 2 2M14 5l2 2-2 2M10 17l-2-2 2-2M14 17l2-2-2-2"/>'
  );
}

/** Untitled UI · Type02 */
export function iconFont(): string {
  return wrap(
    '<path d="M4 7c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C5.602 4 6.068 4 7 4h10c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C20 5.602 20 6.068 20 7M8 20h8M10.25 4v16m3.5-16v16"/>'
  );
}

/** Untitled UI · Ruler — reading guide line */
export function iconReadingGuide(): string {
  return wrap(
    '<path d="M14.5 5.5 16 7m-4.5 1.5L13 10m-4.5 1.5L10 13m-4.5 1.5L7 16m-4.434 1.566 3.868 3.868c.198.198.297.297.411.334.1.033.209.033.31 0 .114-.037.213-.136.41-.334l13.87-13.868c.197-.198.296-.297.333-.412a.499.499 0 0 0 0-.309c-.037-.114-.136-.213-.334-.41l-3.868-3.87c-.198-.197-.297-.296-.412-.333a.5.5 0 0 0-.309 0c-.114.037-.213.136-.41.334L2.564 16.434c-.197.198-.296.297-.333.411a.5.5 0 0 0 0 .31c.037.114.136.213.334.41Z"/>'
  );
}

/** Untitled UI · Target01 — focus / concentration */
export function iconFocus(): string {
  return wrap(
    '<path d="M22 12c0 5.523-4.477 10-10 10m10-10c0-5.523-4.477-10-10-10m10 10h-4m-6 10C6.477 22 2 17.523 2 12m10 10v-4M2 12C2 6.477 6.477 2 12 2M2 12h4m6-10v4"/>'
  );
}

/** Untitled UI · PauseCircle */
export function iconAnimationPause(): string {
  return wrap(
    '<path d="M9.5 15V9m5 6V9m7.5 3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"/>'
  );
}

/** Untitled UI · Cursor04 */
export function iconCursor(): string {
  return wrap(
    '<path d="M4.407 3.414c-.458-.135-.687-.202-.843-.144a.5.5 0 0 0-.294.294c-.058.156.009.385.144.843l4.206 14.3c.125.426.188.64.314.738a.5.5 0 0 0 .39.1c.158-.027.315-.184.629-.498L12 16l4.434 4.434c.198.198.297.297.411.335a.5.5 0 0 0 .31 0c.114-.037.213-.136.41-.334l2.87-2.87c.197-.197.296-.296.333-.41a.5.5 0 0 0 0-.31c-.037-.113-.136-.212-.334-.41L16 12l3.047-3.047c.314-.314.47-.47.497-.629a.5.5 0 0 0-.1-.39c-.098-.126-.311-.189-.737-.314l-14.3-4.206Z"/>'
  );
}

/** Untitled UI · VolumeMax */
export function iconSpeaker(): string {
  return wrap(
    '<path d="M19.748 5A11.946 11.946 0 0 1 22 12c0 2.612-.835 5.03-2.252 7M15.745 8A6.968 6.968 0 0 1 17 12a6.967 6.967 0 0 1-1.255 4M9.635 5.366 6.468 8.53c-.173.173-.26.26-.36.322a1 1 0 0 1-.29.12C5.704 9 5.582 9 5.337 9H3.6c-.56 0-.84 0-1.054.109a1 1 0 0 0-.437.437C2 9.76 2 10.04 2 10.6v2.8c0 .56 0 .84.109 1.054a1 1 0 0 0 .437.437C2.76 15 3.04 15 3.6 15h1.737c.245 0 .367 0 .482.028a1 1 0 0 1 .29.12c.1.061.187.148.36.32l3.165 3.166c.429.429.643.643.827.657a.5.5 0 0 0 .42-.174c.119-.14.119-.443.119-1.048V5.93c0-.606 0-.908-.12-1.049a.5.5 0 0 0-.42-.173c-.183.014-.397.228-.826.657Z"/>'
  );
}

/** Untitled UI · Lightbulb01 — cognitive support (kept distinct from Focus) */
export function iconBrain(): string {
  return wrap(
    '<path d="M15 16.5V19c0 .932 0 1.398-.152 1.765a2 2 0 0 1-1.083 1.083C13.398 22 12.932 22 12 22c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C9 20.398 9 19.932 9 19v-2.5m6 0c2.649-1.157 4.5-3.925 4.5-7a7.5 7.5 0 0 0-15 0c0 3.075 1.851 5.843 4.5 7m6 0H9"/>'
  );
}

/** Untitled UI · Image03 — image / alt-text */
export function iconImage(): string {
  return wrap(
    '<path d="m4.272 20.728 6.597-6.597c.396-.396.594-.594.822-.668a1 1 0 0 1 .618 0c.228.074.426.272.822.668l6.553 6.553M14 15l2.869-2.869c.396-.396.594-.594.822-.668a1 1 0 0 1 .618 0c.228.074.426.272.822.668L22 15M10 9a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM6.8 21h10.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C22 18.72 22 17.88 22 16.2V7.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C19.72 3 18.88 3 17.2 3H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 5.28 2 6.12 2 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 21 5.12 21 6.8 21Z"/>'
  );
}

/** Untitled UI · Keyboard01 */
export function iconKeyboard(): string {
  return wrap(
    '<path d="M6 10h.01M8 14h.01M10 10h.01M12 14h.01M14 10h.01M16 14h.01M18 10h.01M5.2 18h13.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C22 16.48 22 15.92 22 14.8V9.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 6 19.92 6 18.8 6H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 7.52 2 8.08 2 9.2v5.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 18 4.08 18 5.2 18Z"/>'
  );
}

/** Untitled UI · Droplets01 — saturation / color blindness */
export function iconColorBlindness(): string {
  return wrap(
    '<path d="M22 16a6 6 0 0 1-12 0c0-4.314 6-14 6-14s6 9.686 6 14ZM8 9a3 3 0 1 1-6 0c0-2.157 3-7 3-7s3 4.843 3 7Z"/>'
  );
}

/** Untitled UI · Link01 */
export function iconLinkHighlight(): string {
  return wrap(
    '<path d="m12.708 18.364-1.415 1.414a5 5 0 1 1-7.07-7.07l1.413-1.415m12.728 1.414 1.415-1.414a5 5 0 0 0-7.071-7.071l-1.415 1.414M8.5 15.5l7-7"/>'
  );
}

/** Untitled UI · XClose */
export function iconClose(): string {
  return wrap('<path d="M18 6 6 18M6 6l12 12"/>');
}

/** Untitled UI · RefreshCw02 */
export function iconReset(): string {
  return wrap('<path d="M2 14s.121.85 3.636 4.364A9 9 0 0 0 20.776 14M2 14v6m0-6h6m14-4s-.121-.85-3.636-4.364A9 9 0 0 0 3.224 10M22 10V4m0 6h-6"/>');
}

/** Untitled UI · ChevronRight */
export function iconChevron(): string {
  return wrap('<path d="m9 18 6-6-6-6"/>');
}

/** Untitled UI · Check */
export function iconCheckmark(): string {
  return wrap('<path d="M20 6 9 17l-5-5"/>');
}

/** Untitled UI · AlertTriangle */
export function iconWarning(): string {
  return wrap(
    '<path d="M12 9v4m0 4h.01M10.615 3.892 2.39 18.098c-.456.788-.684 1.182-.65 1.506a1 1 0 0 0 .406.705c.263.191.718.191 1.629.191h16.45c.91 0 1.365 0 1.628-.191a1 1 0 0 0 .407-.705c.034-.324-.195-.718-.65-1.506L13.383 3.892c-.454-.785-.681-1.178-.978-1.31a1 1 0 0 0-.813 0c-.296.132-.523.525-.978 1.31Z"/>'
  );
}

/** Untitled UI · SearchSm */
export function iconScan(): string {
  return wrap('<path d="m21 21-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"/>');
}

/** Untitled UI · BookOpen01 — simplify-text */
export function iconSimplifyText(): string {
  return wrap(
    '<path d="m12 21-.1-.15c-.695-1.042-1.042-1.563-1.5-1.94a4 4 0 0 0-1.378-.737C8.453 18 7.827 18 6.575 18H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 16.48 2 15.92 2 14.8V6.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 3 4.08 3 5.2 3h.4c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C12 6.04 12 7.16 12 9.4M12 21V9.4M12 21l.1-.15c.695-1.042 1.042-1.563 1.5-1.94a3.999 3.999 0 0 1 1.378-.737C15.547 18 16.173 18 17.425 18H18.8c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C22 16.48 22 15.92 22 14.8V6.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 3 19.92 3 18.8 3h-.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C12 6.04 12 7.16 12 9.4"/>'
  );
}

// ---------------------------------------------------------------------------
// Profile + section icons (Untitled UI)
// ---------------------------------------------------------------------------

/** Untitled UI · Eye — vision-impaired profile */
export function iconEye(): string {
  return wrap(
    '<path d="M2.42 12.713c-.136-.215-.204-.323-.242-.49a1.173 1.173 0 0 1 0-.446c.038-.167.106-.274.242-.49C3.546 9.505 6.895 5 12 5s8.455 4.505 9.58 6.287c.137.215.205.323.243.49.029.125.029.322 0 .446-.038.167-.106.274-.242.49C20.455 14.495 17.105 19 12 19c-5.106 0-8.455-4.505-9.58-6.287Z"/>' +
    '<path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/>'
  );
}

/** Untitled UI · Shield01 — seizure-safe profile */
export function iconShield(): string {
  return wrap(
    '<path d="M11.302 21.615c.221.129.332.194.488.227.122.026.298.026.42 0 .156-.034.267-.098.488-.227C14.646 20.478 20 16.908 20 12V7.217c0-.799 0-1.199-.13-1.542a2 2 0 0 0-.548-.79c-.275-.243-.65-.383-1.398-.664l-5.362-2.01c-.208-.078-.312-.117-.419-.133a1 1 0 0 0-.286 0c-.107.016-.21.055-.419.133L6.076 4.22c-.748.28-1.122.421-1.398.664a2 2 0 0 0-.547.79C4 6.018 4 6.418 4 7.217V12c0 4.908 5.354 8.478 7.302 9.615Z"/>'
  );
}

/** Untitled UI · Droplets01 */
export function iconSaturation(): string {
  return wrap(
    '<path d="M22 16a6 6 0 0 1-12 0c0-4.314 6-14 6-14s6 9.686 6 14ZM8 9a3 3 0 1 1-6 0c0-2.157 3-7 3-7s3 4.843 3 7Z"/>'
  );
}

/** Untitled UI · ImageX — hide images */
export function iconHideImages(): string {
  return wrap(
    '<path d="m16.5 2.5 5 5m0-5-5 5m-4-4.5H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 5.28 3 6.12 3 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 21 6.12 21 7.8 21H17c.93 0 1.395 0 1.776-.102a3 3 0 0 0 2.122-2.122C21 18.395 21 17.93 21 17M10.5 8.5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm4.49 3.418-8.459 7.69c-.476.433-.714.649-.735.836a.5.5 0 0 0 .167.431C6.105 21 6.426 21 7.07 21h9.387c1.44 0 2.159 0 2.724-.242a3 3 0 0 0 1.578-1.578c.242-.565.242-1.285.242-2.724 0-.484 0-.726-.053-.952a2.001 2.001 0 0 0-.374-.778c-.143-.182-.332-.333-.71-.636l-2.797-2.237c-.379-.303-.568-.454-.776-.508a1 1 0 0 0-.557.018c-.205.066-.384.23-.743.555Z"/>'
  );
}

/** Untitled UI · AlignLeft */
export function iconTextAlign(): string {
  return wrap('<path d="M16 10H3m17-4H3m17 8H3m13 4H3"/>');
}

/** Untitled UI · Maximize02 — reading mask (focus area) */
export function iconReadingMask(): string {
  return wrap(
    '<path d="M8 3h-.2c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 5.28 3 6.12 3 7.8V8m5 13h-.2c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C3 18.72 3 17.88 3 16.2V16m18-8v-.2c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 3 17.88 3 16.2 3H16m5 13v.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 21 17.88 21 16.2 21H16"/>'
  );
}

/** Untitled UI · LayoutLeft — page structure / outline */
export function iconPageStructure(): string {
  return wrap(
    '<path d="M9 3v18M7.8 3h8.4c1.68 0 2.52 0 3.162.327a3 3 0 0 1 1.311 1.311C21 5.28 21 6.12 21 7.8v8.4c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 21 17.88 21 16.2 21H7.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C3 18.72 3 17.88 3 16.2V7.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C5.28 3 6.12 3 7.8 3Z"/>'
  );
}

/** Untitled UI · Type01 — also used for line-height */
export function iconLineHeight(): string {
  return wrap(
    '<path d="M4 7c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C5.602 4 6.068 4 7 4h10c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C20 5.602 20 6.068 20 7M9 20h6M12 4v16"/>'
  );
}

/** Untitled UI · Globe01 */
export function iconGlobe(): string {
  return wrap(
    '<path d="M2 12h20M2 12c0 5.523 4.477 10 10 10M2 12C2 6.477 6.477 2 12 2m10 10c0 5.523-4.477 10-10 10m10-10c0-5.523-4.477-10-10-10m0 0a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10m0-20a15.3 15.3 0 0 0-4 10 15.3 15.3 0 0 0 4 10"/>'
  );
}

/** Untitled UI · User01 */
export function iconProfile(): string {
  return wrap(
    '<path d="M20 21c0-1.396 0-2.093-.172-2.661a4 4 0 0 0-2.667-2.667c-.568-.172-1.265-.172-2.661-.172h-5c-1.396 0-2.093 0-2.661.172a4 4 0 0 0-2.667 2.667C4 18.907 4 19.604 4 21M16.5 7.5a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"/>'
  );
}

/** Untitled UI · MessageSquare02. Same speech-bubble glyph the Dashboard
 *  sidebar uses for "Feedback" — keeps the brand-wide visual language
 *  consistent. Replaces the 💬 emoji that was hardcoded in FeedbackForm. */
export function iconFeedback(): string {
  return wrap(
    '<path d="M3 7.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C5.28 3 6.12 3 7.8 3h8.4c1.68 0 2.52 0 3.162.327a3 3 0 0 1 1.311 1.311C21 5.28 21 6.12 21 7.8v5.4c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 18 17.88 18 16.2 18h-2.516c-.624 0-.936 0-1.235.061a2.997 2.997 0 0 0-.761.267c-.272.14-.516.334-1.003.724L8.3 20.96c-.416.333-.624.5-.8.5a.5.5 0 0 1-.39-.188C7 21.135 7 20.868 7 20.336V18c-.93 0-1.395 0-1.776-.102a3 3 0 0 1-2.122-2.121C3 15.395 3 14.93 3 14V7.8Z"/>'
  );
}

/** Gear — the widget's own settings menu ("Einstellungsrad"). Same 2px
 *  outline stroke style as the rest of the set. */
export function iconSettings(): string {
  return wrap(
    '<circle cx="12" cy="12" r="3"/>' +
    '<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>'
  );
}

// ---------------------------------------------------------------------------
// Aggregate export - look-up map keyed by feature slug
// ---------------------------------------------------------------------------

/** Untitled UI · SearchLg — text magnifier */
export function iconMagnifier(): string {
  return wrap('<path d="m21 21-4.35-4.35"/><circle cx="11" cy="11" r="8"/>');
}

/** Untitled UI · VolumeXmark — mute media */
export function iconMute(): string {
  return wrap('<path d="M11 5 6 9H2v6h4l5 4V5Z"/><path d="m22 9-6 6M16 9l6 6"/>');
}

/** Untitled UI · Maximize01 — page zoom */
export function iconPageZoom(): string {
  return wrap('<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>');
}

/** Untitled UI · Edit05 — highlight on hover */
export function iconCursorHighlight(): string {
  return wrap('<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"/>');
}

export const icons: Record<string, () => string> = {
  accessibility: iconAccessibility,
  contrast: iconContrast,
  'text-size': iconTextSize,
  spacing: iconSpacing,
  font: iconFont,
  'dyslexia-font': iconFont,
  'reading-guide': iconReadingGuide,
  focus: iconFocus,
  'animation-pause': iconAnimationPause,
  cursor: iconCursor,
  speaker: iconSpeaker,
  tts: iconSpeaker,
  brain: iconBrain,
  ai: iconBrain,
  image: iconImage,
  'alt-text': iconImage,
  keyboard: iconKeyboard,
  'color-blindness': iconColorBlindness,
  'link-highlight': iconLinkHighlight,
  close: iconClose,
  'close-x': iconClose,
  reset: iconReset,
  chevron: iconChevron,
  checkmark: iconCheckmark,
  warning: iconWarning,
  scan: iconScan,
  'simplify-text': iconSimplifyText,
  // New icons
  eye: iconEye,
  shield: iconShield,
  saturation: iconSaturation,
  magnifier: iconMagnifier,
  mute: iconMute,
  'page-zoom': iconPageZoom,
  'cursor-highlight': iconCursorHighlight,
  'hide-images': iconHideImages,
  'text-align': iconTextAlign,
  'reading-mask': iconReadingMask,
  'page-structure': iconPageStructure,
  'line-height': iconLineHeight,
  globe: iconGlobe,
  profile: iconProfile,
  feedback: iconFeedback,
  settings: iconSettings,
};
